// ============================================================================
// PATIENT FINANCIAL BLOCK — Resumen financiero del paciente
// KPIs + gráfica de barras mensual (últimos 12 meses) en SVG nativo
// Sin dependencias externas (no usa recharts)
// ============================================================================
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Euro } from 'lucide-react';
import { budgetsApi } from '../../api/budgets';
import type { PatientFinancialSummary, MonthlyAggregate } from '../../types/patient';
import { CollapsibleSection } from '../ui/CollapsibleSection';

interface PatientFinancialBlockProps {
  patientId: string;
  userId?: string;
}

function formatEuro(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formatEuroCompact(n: number): string {
  if (n === 0) return '0';
  if (Math.abs(n) >= 1000) {
    return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.', ',') + 'k';
  }
  return n.toFixed(0);
}

interface SummaryCardProps {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}

function SummaryCard({ label, value, tone = 'default' }: SummaryCardProps) {
  const tones: Record<string, string> = {
    default: 'bg-gray-50 text-gray-900 border-gray-200',
    success: 'bg-green-50 text-green-900 border-green-200',
    warning: 'bg-amber-50 text-amber-900 border-amber-200',
    danger: 'bg-red-50 text-red-900 border-red-200',
  };

  return (
    <div className={`p-3 rounded-lg border ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-semibold mt-1">{formatEuro(value)}</p>
    </div>
  );
}

// ─── Gráfica de barras nativa en SVG ───────────────────────────────────────
interface MonthlyBarChartProps {
  data: MonthlyAggregate[];
}

interface TooltipState {
  x: number;
  y: number;
  label: string;
  billed: number;
  collected: number;
}

function MonthlyBarChart({ data }: MonthlyBarChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const { maxValue, ticks } = useMemo(() => {
    const values = data.flatMap((d) => [d.billed, d.collected]);
    const rawMax = Math.max(1, ...values);
    // Redondear hacia arriba a una escala "agradable"
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const niceMax = Math.ceil(rawMax / magnitude) * magnitude;
    const step = niceMax / 4;
    const t: number[] = [];
    for (let i = 0; i <= 4; i++) t.push(step * i);
    return { maxValue: niceMax, ticks: t };
  }, [data]);

  // Dimensiones del viewBox (responsive por %)
  const WIDTH = 700;
  const HEIGHT = 260;
  const MARGIN = { top: 10, right: 16, bottom: 32, left: 56 };
  const chartWidth = WIDTH - MARGIN.left - MARGIN.right;
  const chartHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

  const bandWidth = chartWidth / Math.max(1, data.length);
  const barWidth = Math.min(14, bandWidth / 3);
  const barGap = 2;

  const yScale = (v: number) => chartHeight - (v / maxValue) * chartHeight;

  return (
    <div className="w-full relative">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-64"
        preserveAspectRatio="none"
        role="img"
        aria-label="Evolución mensual de facturado y cobrado"
      >
        {/* Grid + ticks Y */}
        {ticks.map((t, i) => (
          <g key={i} transform={`translate(${MARGIN.left}, ${MARGIN.top + yScale(t)})`}>
            <line x1={0} x2={chartWidth} y1={0} y2={0} stroke="#f1f5f9" strokeWidth={1} />
            <text
              x={-8}
              y={3}
              textAnchor="end"
              fontSize={10}
              fill="#64748b"
            >
              {formatEuroCompact(t)}
            </text>
          </g>
        ))}

        {/* Barras + etiquetas X */}
        {data.map((d, i) => {
          const cx = MARGIN.left + i * bandWidth + bandWidth / 2;
          const billedH = Math.max(0, chartHeight - yScale(d.billed));
          const collectedH = Math.max(0, chartHeight - yScale(d.collected));
          const billedX = cx - barWidth - barGap / 2;
          const collectedX = cx + barGap / 2;

          return (
            <g
              key={i}
              onMouseEnter={() => setTooltip({
                x: (cx / WIDTH) * 100,
                y: (MARGIN.top / HEIGHT) * 100,
                label: d.label,
                billed: d.billed,
                collected: d.collected,
              })}
              onMouseLeave={() => setTooltip(null)}
            >
              {/* Zona hover invisible para mejor UX */}
              <rect
                x={MARGIN.left + i * bandWidth}
                y={MARGIN.top}
                width={bandWidth}
                height={chartHeight}
                fill="transparent"
              />
              {/* Barra facturado */}
              {d.billed > 0 && (
                <rect
                  x={billedX}
                  y={MARGIN.top + yScale(d.billed)}
                  width={barWidth}
                  height={billedH}
                  fill="#008DD0"
                  rx={2}
                />
              )}
              {/* Barra cobrado */}
              {d.collected > 0 && (
                <rect
                  x={collectedX}
                  y={MARGIN.top + yScale(d.collected)}
                  width={barWidth}
                  height={collectedH}
                  fill="#22c55e"
                  rx={2}
                />
              )}
              {/* Etiqueta X */}
              <text
                x={cx}
                y={MARGIN.top + chartHeight + 16}
                textAnchor="middle"
                fontSize={10}
                fill="#64748b"
              >
                {d.label}
              </text>
            </g>
          );
        })}

        {/* Eje X */}
        <line
          x1={MARGIN.left}
          x2={MARGIN.left + chartWidth}
          y1={MARGIN.top + chartHeight}
          y2={MARGIN.top + chartHeight}
          stroke="#e2e8f0"
          strokeWidth={1}
        />
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs"
          style={{
            left: `${tooltip.x}%`,
            top: `${tooltip.y}%`,
            transform: 'translate(-50%, -110%)',
            minWidth: '140px',
          }}
        >
          <p className="font-semibold text-gray-900 mb-1">{tooltip.label}</p>
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 text-gray-600">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#008DD0' }} />
              Facturado
            </span>
            <span className="font-medium text-gray-900">{formatEuro(tooltip.billed)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 mt-0.5">
            <span className="inline-flex items-center gap-1.5 text-gray-600">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
              Cobrado
            </span>
            <span className="font-medium text-gray-900">{formatEuro(tooltip.collected)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bloque principal ──────────────────────────────────────────────────────
export function PatientFinancialBlock({ patientId, userId }: PatientFinancialBlockProps) {
  const [data, setData] = useState<PatientFinancialSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = useCallback(async () => {
    if (hasLoaded) return;
    try {
      const result = await budgetsApi.getPatientFinancialSummary(patientId);
      setData(result);
      setHasLoaded(true);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar el resumen financiero');
    }
  }, [patientId, hasLoaded]);

  useEffect(() => { load(); }, [load]);

  const hasAnyData = data?.monthly.some((m) => m.billed > 0 || m.collected > 0) ?? false;

  return (
    <CollapsibleSection
      id="financial"
      title="Resumen financiero"
      icon={<Euro className="h-5 w-5 text-gray-400" />}
      defaultOpen={false}
      userId={userId}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}

      {!data && !error ? (
        <p className="text-sm text-gray-400">Cargando...</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <SummaryCard label="Facturado" value={data.totalBilled} />
            <SummaryCard label="Cobrado" value={data.totalCollected} tone="success" />
            <SummaryCard
              label="Pendiente"
              value={data.totalPending}
              tone={data.totalPending > 0 ? 'warning' : 'default'}
            />
            <SummaryCard
              label="Devoluciones"
              value={data.totalRefunded}
              tone={data.totalRefunded > 0 ? 'danger' : 'default'}
            />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Evolución mensual (últimos 12 meses)
            </p>
            {!hasAnyData ? (
              <p className="text-sm text-gray-500 italic py-4">
                Sin actividad registrada en los últimos 12 meses.
              </p>
            ) : (
              <>
                <MonthlyBarChart data={data.monthly} />
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-sm"
                      style={{ backgroundColor: '#008DD0' }}
                    />
                    Facturado
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-sm"
                      style={{ backgroundColor: '#22c55e' }}
                    />
                    Cobrado
                  </span>
                </div>
              </>
            )}
          </div>
        </>
      ) : null}
    </CollapsibleSection>
  );
}

export default PatientFinancialBlock;
